import { Building2 } from 'lucide-react';
import type { OrgUnitReport, OrgUnitReportRow } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { cn } from '@eombor/ui';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { ReportCard } from './ReportCard.js';

/**
 * KORXONALAR REYTINGI — MTU rahbarining solishtirma ko'rinishi.
 *
 * Uch raqam bitta qatorda: qancha SO'RADI, qanchasi hali YO'LDA va
 * o'rtacha necha KUNDA olindi. Ular birga o'qilganda savol aniq bo'ladi
 * — ko'p so'ragan korxona sekin olayotgan bo'lsa, muammo omborda;
 * kam so'ragan korxona uzoq kutayotgan bo'lsa, muammo tasdiqlashda.
 *
 * Korxona rahbari bu blokda O'Z korxonasining bitta satrini ko'radi —
 * doira serverda kesiladi va bu yerda alohida filtr yo'q.
 */

const format = (value: number): string => value.toLocaleString('ru-RU');

/** Bajarilgan ulush — yopilganning so'ralganga nisbati. */
const doneRatio = (row: OrgUnitReportRow): number =>
  row.requested === 0 ? 0 : row.closed / row.requested;

export interface OrgUnitBlockProps {
  query: UseQueryResult<OrgUnitReport, ApiClientError>;
}

export const OrgUnitBlock = ({ query }: OrgUnitBlockProps) => {
  const rows = query.data?.rows ?? [];
  const max = Math.max(1, ...rows.map((row) => row.requested));

  return (
    <ReportCard
      icon={Building2}
      tone="accent"
      title={t('report.orgTitle')}
      hint={t('report.orgHint')}
      pending={query.isPending}
      error={query.error ?? null}
      empty={rows.length === 0}
      emptyText={t('report.orgEmpty')}
    >
      {/*
        IKKI USTUN keng ekranda: bitta korxona qolganda chiziq butun
        1300 piksel bo'ylab cho'zilib, karta buzilgandek ko'rinardi —
        va nomi bilan soni bir-biridan shunchalik uzoqlashardiki,
        ularni ko'z bilan bog'lab bo'lmasdi.
      */}
      <ul className="grid gap-x-8 gap-y-3 lg:grid-cols-2">
        {rows.map((row) => {
          const ratio = doneRatio(row);
          return (
            <li key={row.orgUnitId} className="flex flex-col gap-1.5">
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-sm font-medium text-text">{row.name}</span>
                <span className="tabular shrink-0 text-sm text-text">
                  {format(row.requested)}
                  <span className="text-text-faint"> {t('report.orgRequested')}</span>
                </span>
              </span>

              {/*
                Bitta chiziqda IKKI qatlam: umumiy uzunlik — so'rov
                soni (korxonalar bir-biriga nisbatan), ichidagi to'q
                qism — bajarilgani. Ikki alohida chiziq bo'lsa,
                «ko'p so'radi» bilan «ko'p oldi» aralashib ketardi.
              */}
              <span
                aria-hidden
                className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
              >
                <span
                  className="block h-full overflow-hidden rounded-full bg-accent-soft"
                  style={{ width: `${String(Math.max(3, (row.requested / max) * 100))}%` }}
                >
                  <span
                    className="block h-full rounded-full bg-accent"
                    style={{ width: `${String(Math.round(ratio * 100))}%` }}
                  />
                </span>
              </span>

              <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                <span>{t('report.orgClosed', { count: row.closed })}</span>
                <span className={cn(row.openCount > 0 ? 'text-warn' : undefined)}>
                  {t('report.orgOpen', { count: row.openCount })}
                </span>
                {/* Nol rad etish — normal hol, urg'u talab qilmaydi. */}
                {row.rejected > 0 ? (
                  <span className="text-danger">{t('report.orgRejected', { count: row.rejected })}</span>
                ) : null}
                <span>
                  {row.avgCycleDays === null
                    ? t('report.orgNoCycle')
                    : t('report.orgCycle', { days: row.avgCycleDays })}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </ReportCard>
  );
};
